import * as moment from 'moment/moment';
import {
    ButtonBubble,
    ContentInteractive,
    ContentLocation,
    ContentMedia,
    LocationRequestBubble,
    metaData,
    OutboundTranslator,
    TranslationResponse
} from './translatorUtils';
import {EmulatorMessage} from '../../../../../shared/models';

interface MediaFile {
    contentType: string;
    downloadUrl: string;
    sha256Hash: string;
    byteSize: number;
    fileName: string;
}

interface GafResponse {
    integrationId: string;
    integrationName: string;
    event: {};
}

interface SelectedItem {
    postbackData: string;
    content?: string;
}

interface MoText {
    channel: string;
    messageId: string;
    relatedMessageId: any;
    relatedClientMessageId: any;
    from: string;
    to: string;
    timestamp: number;
    content: string;
    charset: string;
    automatedResponse?: boolean;
    abc?: {};
    device?: {};
}

interface MoButton {
    channel: string;
    messageId: string;
    relatedMessageId: any;
    relatedClientMessageId: any;
    from: string;
    to: string;
    timestamp: number;
    postbackData: string;
    selectedItem: SelectedItem;
}

interface MoLocation {
    charset: string;
    locationName: any;
    latitude: any;
    channel: string;
    messageId: string;
    locationAddress: any;
    relatedMessageId: any;
    from: string;
    to: string;
    relatedClientMessageId: null;
    timestamp: number;
    longitude: any;
}

interface MoMedia {
    channel: string;
    messageId: string;
    relatedMessageId: any;
    relatedClientMessageId: any;
    from: string;
    to: string;
    timestamp: number;
    caption: string;
    files: MediaFile[];
}

const CHANNEL_NAME = 'rcs';

function image(emulatorMessage: EmulatorMessage, fileMetaData: any): any {
    let description = '';
    if (emulatorMessage['channelPayload']['media'] && emulatorMessage['channelPayload']['media']['caption']) {
        description = emulatorMessage['channelPayload']['media']['caption'];
    }
    return {
        src: fileMetaData['src'],
        description: description,
        bubbleType: 'image',
    };
}

function video(emulatorMessage: EmulatorMessage, fileMetaData: any): any {
    let description = '';

    if (emulatorMessage['channelPayload']['media'] &&
        emulatorMessage['channelPayload']['media']['caption']) {
        description = emulatorMessage['channelPayload']['media']['caption'];
    }

    return {
        src: fileMetaData['src'],
        type: fileMetaData['type'],
        bubbleType: 'video',
        description: description
    };
}

function application(fileMetaData: any): any {
    return {
        type: fileMetaData['type'],
        src: fileMetaData['src'],
        description: fileMetaData['name'],
        fileSize: fileMetaData['size'],
        bubbleType: 'file',
    };
}

function audio(fileMetaData: any): any {
    return {
        src: fileMetaData['src'],
        bubbleType: 'voicenote',
    };
}

export class YagaTranslatorInbound {

    static messagePayload(emulatorMessage: EmulatorMessage): EmulatorMessage {

        const channelPayload = emulatorMessage['channelPayload'] || {};

        if (channelPayload['button']) {
            return YagaTranslatorInbound.button(emulatorMessage, channelPayload);
        }

        if (channelPayload['location']) {
            return YagaTranslatorInbound.location(emulatorMessage, channelPayload);
        }

        if (channelPayload['locationRequest']) {
            return YagaTranslatorInbound.locationRequest(emulatorMessage, channelPayload);
        }

        if (channelPayload['media']) {
            return YagaTranslatorInbound.media(emulatorMessage, channelPayload);
        }

        return YagaTranslatorInbound.text(emulatorMessage);
    }

    private static text(emulatorMessage: EmulatorMessage): EmulatorMessage {
        emulatorMessage.mediaMetaData = {
            src: '',
            bubbleType: 'text',
            content: emulatorMessage['channelPayload']['content'],
        };
        return emulatorMessage;
    }

    private static button(emulatorMessage: EmulatorMessage, channelPayload: any): EmulatorMessage {

        const channelPayloadButton = channelPayload['button'];

        const buttonBubble: ButtonBubble = {
            items: channelPayloadButton['items'] || [],
            body: channelPayloadButton['body'] || undefined,
        };

        if (channelPayloadButton['header'] && channelPayloadButton['header']['media']) {
            const ref = channelPayloadButton['header']['media']['fileId'];
            const fileMetaData = emulatorMessage['channelPayload']['emulatorFiles'][ref];
            const type = fileMetaData['type'].split('/')[0];

            switch (type) {
                case 'image':
                    buttonBubble.media = image(emulatorMessage, fileMetaData) as any;
                    break;
                case 'video':
                    buttonBubble.media = video(emulatorMessage, fileMetaData) as any;
                    break;
                case 'application':
                    buttonBubble.media = application(fileMetaData) as any;
                    break;
            }
        } else if (channelPayloadButton['header'] && channelPayloadButton['header']['text']) {
            buttonBubble.text = channelPayloadButton['header']['text'];
        }

        emulatorMessage.mediaMetaData = {
            src: '',
            bubbleType: 'button',
            buttons: buttonBubble
        };

        return emulatorMessage;
    }

    private static location(emulatorMessage: EmulatorMessage, channelPayload: any): EmulatorMessage {
        emulatorMessage.mediaMetaData = {
            locationName: channelPayload['location'].name,
            locationAddress: channelPayload['location'].address,
            latitude: channelPayload['location'].latitude,
            longitude: channelPayload['location'].longitude,
            src: '',
            bubbleType: 'location',
        };
        return emulatorMessage;
    }

    private static locationRequest(emulatorMessage: EmulatorMessage, channelPayload: any): EmulatorMessage {
        const locationRequest = channelPayload['locationRequest'];
        const locationRequestBubble: LocationRequestBubble = {
            text: undefined,
            body: locationRequest.body || undefined,
            src: undefined
        }

        if (locationRequest['header'] && locationRequest['header']['type'] === 'text') {
            locationRequestBubble.text = locationRequest['header']['text'];
        }

        const fileId = locationRequest?.header?.media?.fileId;
        if (locationRequest['header'] && locationRequest['header']['type'] === 'media' && fileId) {
            locationRequestBubble.src = channelPayload['emulatorFiles'][fileId].src;
        }

        emulatorMessage.mediaMetaData = {
            src: '',
            bubbleType: 'requestlocation',
            locationRequest: locationRequestBubble
        };
        return emulatorMessage;
    }

    private static media(emulatorMessage: EmulatorMessage, channelPayload: any): EmulatorMessage {
        const ref = channelPayload['media']['fileId'];
        const fileMetaData = emulatorMessage['channelPayload']['emulatorFiles'][ref];
        const type = fileMetaData['type'].split('/')[0];
        let mediaMetaData = undefined;
        switch (type) {
            case 'image':
                mediaMetaData = image(emulatorMessage, fileMetaData);
                break;
            case 'video':
                mediaMetaData = video(emulatorMessage, fileMetaData);
                break;
            case 'application':
                mediaMetaData = application(fileMetaData);
                break;
            case 'audio':
                mediaMetaData = audio(fileMetaData);
                break;
        }
        emulatorMessage.mediaMetaData = mediaMetaData;
        return emulatorMessage;
    }
}

class YagaTranslatorOutbound {

    translate: OutboundTranslator;
    gafResponse: GafResponse = {
        integrationId: '',
        integrationName: '',
        event: null,
    };

    messageId = 'test-message-' + Math.floor(Math.random() * 100000);

    constructor(translate: OutboundTranslator) {
        this.translate = translate;
        this.gafResponse.integrationId = translate.integrationId;
        this.gafResponse.integrationName = 'Test Integration';
    }

    private set event(moEvent: {}) {
        this.gafResponse.event = moEvent;
    }

    get text() {
        const moText: MoText = {
            channel: CHANNEL_NAME,
            messageId: this.messageId,
            relatedMessageId: null,
            relatedClientMessageId: null,
            from: this.translate.to,
            to: this.translate.integrationId,
            timestamp: moment.now(),
            content: this.translate.content as string,
            charset: 'UTF-8',
        };

        this.event = {moText: [moText]};
        return this.gafResponse;
    }

    get button() {
        const content: ContentInteractive = this.translate.content as ContentInteractive;

        const moButton: MoButton = {
            channel: CHANNEL_NAME,
            messageId: this.messageId,
            relatedMessageId: null,
            relatedClientMessageId: content.relatedClientMessageId,
            from: this.translate.to,
            to: this.translate.integrationId,
            timestamp: moment.now(),
            postbackData: null,
            selectedItem: {
                postbackData: content.postbackData,
                content: content.messageDisplay
            },
        };

        this.event = {moButtonResponse: [moButton]};
        return this.gafResponse;
    }

    get location() {
        const content: ContentLocation = this.translate.content as ContentLocation;
        const moLocation: MoLocation = {
            channel: CHANNEL_NAME,
            messageId: this.messageId,
            relatedMessageId: null,
            relatedClientMessageId: null,
            from: this.translate.to,
            to: this.translate.integrationId,
            timestamp: moment.now(),
            charset: 'UTF-8',
            locationAddress: content.locationAddress,
            locationName: content.locationName,
            latitude: content.latitude,
            longitude: content.longitude,
        };
        this.event = {moLocation: [moLocation]};
        return this.gafResponse;
    }

    get media() {
        const content: ContentMedia = this.translate.content as ContentMedia;
        const moMedia: MoMedia = {
            channel: CHANNEL_NAME,
            messageId: this.messageId,
            relatedMessageId: null,
            relatedClientMessageId: null,
            from: this.translate.to,
            to: this.translate.integrationId,
            timestamp: moment.now(),
            files: [
                {
                    contentType: content.contentType,
                    downloadUrl: content.downloadUrl,
                    sha256Hash: content.sha256Hash,
                    byteSize: content.byteSize,
                    fileName: content.fileName,
                },
            ],
            caption: content.caption,
        };

        this.event = {moMedia: [moMedia]};
        return this.gafResponse;
    }
}


const translateToYaga = (translate: OutboundTranslator): TranslationResponse => {

    const gaf = new YagaTranslatorOutbound(translate);
    const translationResponse: TranslationResponse = {
        channelPayload: {},
        mediaData: {
            src: '',
            messageId: 'test-message-' + Math.floor(Math.random() * 100000),
            bubbleType: 'text',
        },
    };

    switch (translate.type) {
        case "text":
            translationResponse.channelPayload = gaf.text;
            translationResponse.mediaData.messageId = gaf.messageId;
            translationResponse.mediaData.content = translate.content as string;
            break
        case "button":
            translationResponse.channelPayload = gaf.button;
            translationResponse.mediaData = metaData(translate.content as ContentInteractive, gaf.messageId);
            break;
        case 'location':
            translationResponse.channelPayload = gaf.location;
            translationResponse.mediaData = metaData(translate.content as ContentLocation, gaf.messageId);
            break;
        case 'media':
            translationResponse.channelPayload = gaf.media;
            translationResponse.mediaData = metaData(translate.content as ContentMedia, gaf.messageId);
            break;
        default:
            console.error('Only text is supported for RCS');
            return;
    }

    return translationResponse;
};

export default translateToYaga;
